import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Trash2, Link2, X, Plus, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { TypeIcon, PriorityIcon, Avatar, StatusPill, fmtAgo } from "../components/work/workMeta";
import WorkItemDialog from "../components/work/WorkItemDialog";
import { TimeTracking, WorklogList, fmtMoney } from "../components/work/WorkLog";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

const NONE = "__none__";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

function EditableText({ value, onSave, multiline, placeholder, testId, className = "", disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);
  const commit = async () => { setEditing(false); if ((draft || "") !== (value || "")) await onSave(draft); };
  if (!editing) {
    return (
      <div data-testid={testId} onClick={() => !disabled && setEditing(true)}
        className={`${disabled ? "" : "cursor-text hover:bg-secondary/60"} rounded px-1 -mx-1 ${multiline ? "whitespace-pre-wrap min-h-[2rem]" : ""} ${!value ? "text-muted-foreground italic" : ""} ${className}`}>
        {value || placeholder}
      </div>
    );
  }
  return multiline
    ? <Textarea autoFocus data-testid={`${testId}-input`} rows={5} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} />
    : <Input autoFocus data-testid={`${testId}-input`} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }} className={className} />;
}

function Prop({ label, children, testId }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-2 text-sm py-1" data-testid={testId}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function CommentBox({ members, onSubmit }) {
  const { t } = useLang();
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [pick, setPick] = useState(null);
  const ref = useRef(null);
  const onChange = (e) => {
    const v = e.target.value; setText(v);
    const m = v.slice(0, e.target.selectionStart).match(/@([\w.]*)$/);
    setPick(m ? m[1].toLowerCase() : null);
  };
  const choose = (u) => {
    const pos = ref.current.selectionStart;
    const before = text.slice(0, pos).replace(/@([\w.]*)$/, `@${u.display_name} `);
    setText(before + text.slice(pos)); setMentions([...new Set([...mentions, u.id])]); setPick(null); ref.current.focus();
  };
  const submit = async () => {
    if (!text.trim()) return;
    const ok = await onSubmit(text, mentions.filter((id) => text.includes(members.find((m) => m.id === id)?.display_name || "\u0000")));
    if (ok) { setText(""); setMentions([]); }
  };
  const cands = pick !== null ? members.filter((m) => m.display_name.toLowerCase().includes(pick)).slice(0, 6) : [];
  return (
    <div className="relative" data-testid="comment-box">
      <Textarea ref={ref} data-testid="comment-input" rows={3} value={text} onChange={onChange} placeholder={t("writeComment")}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }} />
      {cands.length > 0 && (
        <div className="absolute z-20 left-2 bottom-12 bg-white border rounded-md shadow-lg w-64" data-testid="mention-picker">
          {cands.map((u) => <button key={u.id} type="button" data-testid={`mention-option-${u.id}`} onClick={() => choose(u)} className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-secondary text-left"><Avatar name={u.display_name} />{u.display_name}</button>)}
        </div>
      )}
      <div className="flex justify-end mt-2"><Button size="sm" data-testid="comment-submit" className="bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={!text.trim()} onClick={submit}>{t("post")}</Button></div>
    </div>
  );
}

export default function WorkItemPage() {
  const { key } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [childOpen, setChildOpen] = useState(false);
  const [link, setLink] = useState({ type: "BLOCKS", key: "" });
  const [tab, setTab] = useState("activity");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/work-items/${key}`);
      setData(d);
      const [s, sp] = await Promise.all([api.get(`/projects/${d.project.id}/work-summary`), api.get(`/projects/${d.project.id}/sprints`).catch(() => ({ data: [] }))]);
      setSummary(s.data); setSprints(sp.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [key]);
  useEffect(() => { load(); }, [load]);

  if (!data || !summary) return <Skeleton className="h-96 w-full" />;
  const { item, children, relations, activity, history, transitions, watchers, access, parent, project, worklogs = [] } = data;
  const canEdit = access !== "READ";

  const patch = async (payload) => {
    try { const { data: d } = await api.patch(`/work-items/${item.key}`, { ...payload, version: item.version }); setData(d); toast.success(t("saved")); }
    catch (e) { toast.error(apiError(e)); if (e?.response?.status === 409) load(); }
  };
  const transition = async (toId) => {
    try { const { data: d } = await api.post(`/work-items/${item.key}/transitions`, { to_status_id: toId }); setData(d); toast.success(`${item.key} → ${d.item.status_name}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const comment = async (body, mentions) => {
    try { await api.post(`/work-items/${item.key}/comments`, { body, mentions }); load(); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };
  const addLink = async () => {
    try { await api.post(`/work-items/${item.key}/relations`, { type: link.type, target_key: link.key }); setLink({ ...link, key: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeLink = async (rid) => { try { await api.delete(`/work-items/${item.key}/relations/${rid}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const watch = async () => { const { data: w } = await api.post(`/work-items/${item.key}/watch`); setData({ ...data, item: { ...item, watching: w.watching } }); load(); };
  const remove = async () => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/work-items/${item.key}`); toast.success(t("deleteItem")); navigate(`/projects/${project.id}/board`); } catch (e) { toast.error(apiError(e)); }
  };
  const moveSprint = async (sid) => { try { await api.post(`/work-items/${item.key}/sprint`, { sprint_id: sid === NONE ? null : sid }); load(); } catch (e) { toast.error(apiError(e)); } };
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const durations = history.map((h) => ({ ...h, seconds: ((h.left_at ? new Date(h.left_at) : new Date()) - new Date(h.entered_at)) / 1000 }));
  const fmtDur = (s) => (s < 3600 ? `${Math.max(1, Math.round(s / 60))}m` : s < 86400 ? `${(s / 3600).toFixed(1)}h` : `${(s / 86400).toFixed(1)}d`);

  return (
    <div className="space-y-4" data-testid="work-item-page">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="work-breadcrumb">
        <Link to={`/projects/${project.id}/board`} className="hover:text-foreground">{project.key} · {project.name}</Link>
        {parent && <><ChevronRight className="h-3 w-3" /><Link to={`/work/${parent.key}`} className="hover:text-foreground inline-flex items-center gap-1" data-testid="parent-link"><TypeIcon type={parent.type} className="h-3 w-3" />{parent.key}</Link></>}
        <ChevronRight className="h-3 w-3" /><span className="text-foreground font-medium">{item.key}</span>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6 min-w-0">
          <div className="flex items-start gap-3">
            <TypeIcon type={item.type} className="h-5 w-5" />
            <div className="flex-1 min-w-0">
              <EditableText value={item.title} onSave={(v) => patch({ title: v })} testId="wi-title" disabled={!canEdit} className="font-heading text-xl sm:text-2xl font-semibold leading-tight" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill name={item.status_name} category={item.status_category} />
            {item.is_blocked && <span className="text-[10px] font-semibold border rounded px-1.5 py-0.5 text-[#991B1B] bg-[#FEE2E2] border-[#FECACA]">{t("blocked")}</span>}
            {canEdit && transitions.length > 0 && (
              <Select value="" onValueChange={transition}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="transition-select"><SelectValue placeholder={t("moveTo")} /></SelectTrigger>
                <SelectContent>{transitions.map((tr) => <SelectItem key={tr.to_status_id} value={tr.to_status_id} data-testid={`transition-option-${tr.to_status_id}`}>{tr.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <div className="flex-1" />
            <Button size="sm" variant="ghost" className="h-8 text-xs" data-testid="watch-btn" onClick={watch}>{item.watching ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}{item.watching ? t("unwatch") : t("watch")} · {watchers.length}</Button>
            {access === "MANAGE" && <Button size="sm" variant="ghost" className="h-8 text-xs text-[#991B1B]" data-testid="delete-item-btn" onClick={remove}><Trash2 className="h-3.5 w-3.5 mr-1" />{t("deleteItem")}</Button>}
          </div>

          <section className="space-y-1">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("description")}</h2>
            <EditableText multiline value={item.description} placeholder="—" onSave={(v) => patch({ description: v })} testId="wi-description" disabled={!canEdit} className="text-sm" />
          </section>
          <section className="space-y-1">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("acceptanceCriteria")}</h2>
            <EditableText multiline value={item.acceptance_criteria} placeholder="—" onSave={(v) => patch({ acceptance_criteria: v })} testId="wi-ac" disabled={!canEdit} className="text-sm" />
          </section>

          {item.type !== "SUBTASK" && (
            <section className="space-y-2" data-testid="children-section">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("childItems")} ({children.length})</h2>
                {canEdit && <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="add-child-btn" onClick={() => setChildOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" />{t("addChild")}</Button>}
              </div>
              <div className="divide-y border rounded-md">
                {children.map((c) => (
                  <Link key={c.id} to={`/work/${c.key}`} className="flex items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-secondary/50" data-testid={`child-row-${c.key}`}>
                    <TypeIcon type={c.type} /><span className="text-xs text-muted-foreground w-16">{c.key}</span><span className="flex-1 truncate">{c.title}</span>
                    <StatusPill name={c.status_name} category={c.status_category} /><Avatar name={c.assignee_name} />
                  </Link>
                ))}
                {children.length === 0 && <div className="px-2.5 py-2 text-xs text-muted-foreground">{t("noData")}</div>}
              </div>
            </section>
          )}

          <section className="space-y-2" data-testid="links-section">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("links")} ({relations.length})</h2>
            <div className="divide-y border rounded-md">
              {relations.map((r) => (
                <div key={r.id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm" data-testid={`relation-row-${r.id}`}>
                  <span className={`text-[10px] font-semibold w-24 shrink-0 ${r.type.startsWith("BLOCK") ? "text-[#991B1B]" : "text-muted-foreground"}`}>{t(r.type)}</span>
                  <TypeIcon type={r.item.type} /><Link to={`/work/${r.item.key}`} className="text-xs text-muted-foreground w-16 hover:underline">{r.item.key}</Link>
                  <span className="flex-1 truncate">{r.item.title}</span><StatusPill name={r.item.status_name} category={r.item.status_category} />
                  {canEdit && <button data-testid={`relation-remove-${r.id}`} onClick={() => removeLink(r.id)} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
              {relations.length === 0 && <div className="px-2.5 py-2 text-xs text-muted-foreground">{t("noData")}</div>}
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <Select value={link.type} onValueChange={(v) => setLink({ ...link, type: v })}>
                  <SelectTrigger className="h-8 w-36 text-xs" data-testid="link-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["BLOCKS", "BLOCKED_BY", "RELATES", "DUPLICATES"].map((x) => <SelectItem key={x} value={x}>{t(x)}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="h-8 w-40 text-sm uppercase" data-testid="link-key-input" placeholder={t("targetKey")} value={link.key} onChange={(e) => setLink({ ...link, key: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addLink()} />
                <Button size="sm" variant="outline" className="h-8" data-testid="link-add-btn" disabled={!link.key.trim()} onClick={addLink}><Link2 className="h-3.5 w-3.5 mr-1" />{t("linkItem")}</Button>
              </div>
            )}
          </section>

          <section className="space-y-3" data-testid="activity-section">
            <div className="flex items-center gap-3 border-b">
              {["activity", "worklog", "history"].map((x) => (
                <button key={x} data-testid={`tab-${x}`} onClick={() => setTab(x)} className={`px-2 py-1.5 text-sm border-b-2 -mb-px ${tab === x ? "border-[#1E3A8A] text-[#1E3A8A] font-medium" : "border-transparent text-muted-foreground"}`}>{x === "activity" ? t("activity") : x === "worklog" ? `${t("worklog")} (${worklogs.length})` : t("statusHistory")}</button>
              ))}
            </div>
            {tab === "worklog" && <WorklogList worklogs={worklogs} canManage={access === "MANAGE"} meId={user?.id} itemKey={item.key} onDeleted={setData} />}
            {tab === "activity" ? (
              <>
                {canEdit && <CommentBox members={summary.members} onSubmit={comment} />}
                <div className="space-y-3" data-testid="activity-list">
                  {[...activity].reverse().map((a) => (
                    <div key={a.id} className="flex gap-2.5 text-sm" data-testid={`activity-${a.id}`}>
                      <Avatar name={a.actor_name} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-muted-foreground"><span className="font-medium text-foreground">{a.actor_name}</span> {a.kind === "COMMENT" ? "commented" : a.summary} · <span title={fmt(a.created_at)}>{fmtAgo(a.created_at)}</span></div>
                        {a.kind === "COMMENT" && <div className="mt-1 bg-secondary/60 rounded-md px-3 py-2 whitespace-pre-wrap">{a.comment_body}</div>}
                        {a.kind === "COMMENT_DELETED" && <div className="mt-1 text-xs italic text-muted-foreground">(comment deleted)</div>}
                        {a.kind === "FIELD_CHANGE" && a.changes?.length > 0 && (
                          <ul className="mt-1 text-xs text-muted-foreground space-y-0.5">
                            {a.changes.map((c, i) => <li key={i}><span className="font-medium">{c.field}</span>: {String(c.from ?? "—")} → <span className="text-foreground">{String(c.to ?? "—")}</span></li>)}
                          </ul>
                        )}
                        {a.kind === "TRANSITION" && a.changes?.[0] && <div className="mt-1 text-xs"><StatusPill name={a.changes[0].from} category="TODO" /> → <StatusPill name={a.changes[0].to} category={item.status_category} /></div>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : tab === "history" ? (
              <div className="divide-y border rounded-md text-sm" data-testid="history-list">
                {durations.map((h) => (
                  <div key={h.id} className="flex items-center gap-3 px-3 py-1.5">
                    <StatusPill name={h.status_name} category={h.category} />
                    <span className="text-xs text-muted-foreground flex-1">{fmt(h.entered_at)} → {h.left_at ? fmt(h.left_at) : "now"}</span>
                    <span className="text-xs font-medium">{fmtDur(h.seconds)}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        </div>

        <aside className="apha-card p-4 space-y-1 self-start" data-testid="properties-panel">
          <Prop label={t("assignee")} testId="prop-assignee">
            {canEdit ? (
              <Select value={item.assignee_id || NONE} onValueChange={(v) => patch(v === NONE ? { clear: ["assignee_id"] } : { assignee_id: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="assignee-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>{t("unassigned")}</SelectItem>{summary.members.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`assignee-option-${m.id}`}>{m.display_name}</SelectItem>)}</SelectContent>
              </Select>
            ) : <span className="inline-flex items-center gap-1.5"><Avatar name={item.assignee_name} />{item.assignee_name || t("unassigned")}</span>}
          </Prop>
          <Prop label={t("reporter")}><span className="inline-flex items-center gap-1.5"><Avatar name={item.reporter_name} />{item.reporter_name}</span></Prop>
          <Prop label={t("priority")} testId="prop-priority">
            {canEdit ? (
              <Select value={item.priority} onValueChange={(v) => patch({ priority: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{summary.priorities.map((p) => <SelectItem key={p} value={p}>{t(p)}</SelectItem>)}</SelectContent>
              </Select>
            ) : <span className="inline-flex items-center gap-1"><PriorityIcon priority={item.priority} />{t(item.priority)}</span>}
          </Prop>
          <Prop label={t("type")}>
            {canEdit ? (
              <Select value={item.type} onValueChange={(v) => patch({ type: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{summary.types.map((x) => <SelectItem key={x} value={x}>{t(x)}</SelectItem>)}</SelectContent>
              </Select>
            ) : t(item.type)}
          </Prop>
          {project.methodology !== "KANBAN" && item.type !== "SUBTASK" && (
            <Prop label={t("sprint")} testId="prop-sprint">
              {canEdit ? (
                <Select value={item.sprint_id || NONE} onValueChange={moveSprint}>
                  <SelectTrigger className="h-8 text-xs" data-testid="sprint-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE}>{t("noSprint")}</SelectItem>{sprints.filter((s) => s.status !== "COMPLETED" || s.id === item.sprint_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.status === "ACTIVE" ? " ●" : ""}</SelectItem>)}</SelectContent>
                </Select>
              ) : data.sprint?.name || "—"}
            </Prop>
          )}
          <Prop label={t("storyPointsLabel")} testId="prop-points"><EditableText value={item.story_points != null ? String(item.story_points) : ""} placeholder="—" onSave={(v) => patch(v === "" ? { clear: ["story_points"] } : { story_points: num(v) })} testId="points-field" disabled={!canEdit} /></Prop>
          <Prop label={t("estimateHours")} testId="prop-estimate"><EditableText value={item.estimate_hours != null ? String(item.estimate_hours) : ""} placeholder="—" onSave={(v) => patch(v === "" ? { clear: ["estimate_hours"] } : { estimate_hours: num(v) })} testId="estimate-field" disabled={!canEdit} /></Prop>
          <Prop label={t("remainingHours")} testId="prop-remaining"><EditableText value={item.remaining_hours != null ? String(item.remaining_hours) : ""} placeholder="—" onSave={(v) => patch(v === "" ? { clear: ["remaining_hours"] } : { remaining_hours: num(v) })} testId="remaining-field" disabled={!canEdit} /></Prop>
          <TimeTracking item={item} canEdit={canEdit} onLogged={setData} />
          {data.can_see_price && (
            <Prop label={`${t("price")} (${project.currency})`} testId="prop-price">
              <EditableText value={item.price != null ? String(item.price) : ""} placeholder={t("priceOptional")} onSave={(v) => patch(v === "" ? { clear: ["price"] } : { price: num(v) })} testId="price-field" disabled={!canEdit} />
              {item.price != null && <div className="text-[11px] text-muted-foreground" data-testid="price-formatted">{fmtMoney(item.price, project.currency)}{data.children_price_total ? ` · ${t("childItems")}: ${fmtMoney(data.children_price_total, project.currency)}` : ""}</div>}
            </Prop>
          )}
          <Prop label={t("plannedStart")}>{canEdit ? <Input type="date" className="h-8 text-xs" data-testid="planned-start-input" value={item.planned_start || ""} onChange={(e) => patch(e.target.value ? { planned_start: e.target.value } : { clear: ["planned_start"] })} /> : item.planned_start || "—"}</Prop>
          <Prop label={t("dueDate")} testId="prop-due">{canEdit ? <Input type="date" className="h-8 text-xs" data-testid="due-date-input" value={item.due_date || ""} onChange={(e) => patch(e.target.value ? { due_date: e.target.value } : { clear: ["due_date"] })} /> : item.due_date || "—"}</Prop>
          <Prop label={t("labels")} testId="prop-labels"><EditableText value={(item.labels || []).join(", ")} placeholder="—" onSave={(v) => patch({ labels: v.split(",").map((x) => x.trim()).filter(Boolean) })} testId="labels-field" disabled={!canEdit} /></Prop>
          <Prop label={t("parent")}>{parent ? <Link to={`/work/${parent.key}`} className="text-[#1E3A8A] hover:underline">{parent.key}</Link> : "—"}{parent && canEdit && <button className="ml-2 text-xs text-muted-foreground hover:text-foreground" data-testid="clear-parent-btn" onClick={() => patch({ clear: ["parent_id"] })}>×</button>}</Prop>
          <Prop label={t("watchers")}><div className="flex -space-x-1">{watchers.map((w) => <Avatar key={w.id} name={w.name} />)}</div></Prop>
          <div className="pt-2 text-[11px] text-muted-foreground space-y-0.5 border-t mt-2">
            <div>{t("created")}: {fmt(item.created_at)}</div><div>{t("updated")}: {fmt(item.updated_at)}</div>
            {item.resolved_at && <div>{t("done")}: {fmt(item.resolved_at)}</div>}<div>{t("version")} {item.version}</div>
          </div>
        </aside>
      </div>
      <WorkItemDialog open={childOpen} onOpenChange={setChildOpen} summary={summary} sprints={sprints}
        defaults={{ parent_id: item.id, type: item.type === "EPIC" ? "STORY" : "SUBTASK", sprint_id: item.sprint_id || "" }} onCreated={load} />
    </div>
  );
}
